import { useCallback, useEffect, useRef } from 'react';
import { CANCELLED, EMPTY, readClipboardSource, readLocalFileSource, writeClipboardResult } from '../../utils/compareSources';
import { applyHunkToText, applyHunkReverse } from '../../utils/diffMergeActions';
import { monacoWidgetOpen } from '../ui/Modal';
import { errToken } from './fileEditLanguage';

/** Loading, saving with the mtime conflict check, compare / diff hunks, and close guards for the remote file editor. */
export function useFileEditActions({
  t,
  compare,
  isOpen,
  connId,
  path,
  name,
  onClose,
  onSaved,
  local,
  compareWith,
  backend,
  validate,
  loading,
  setLoading,
  mode,
  loadGen,
  setLoadGen,
  loadError,
  setLoadError,
  setContent,
  setSavedContent,
  mtime,
  setMtime,
  saving,
  setSaving,
  setSaveError,
  setSavedFlash,
  setMode,
  setConflict,
  latest,
  setLatest,
  setConfirmDiscard,
  confirmDropLeft,
  setConfirmDropLeft,
  setCompare,
  setCompareMenu,
  setCompareError,
  setHunks,
  compareContent,
  setCompareContent,
  compareDirty,
  setCompareDirty,
  setCopied,
  diffEditorRef,
  dirty,
  saveRef,
  contentRef,
  diffModifiedRef,
}) {
  /**
   * Point the diff widget at nothing before anything can unmount it.
   *
   * @monaco-editor/react disposes the original/modified TextModels in its own
   * unmount cleanup, and Monaco throws "TextModel got disposed before
   * DiffEditorWidget model got reset" if the widget still references them. An
   * effect cleanup here is not a reliable place to do it — React does not
   * guarantee it runs before the child's — so every path that can unmount the
   * DiffEditor calls this synchronously first.
   */
  const detachDiffModels = useCallback(() => {
    try {
      diffEditorRef.current?.setModel(null);
    } catch {
      /* already torn down — nothing to detach */
    }
    diffEditorRef.current = null;
    diffModifiedRef.current = null;
  }, [diffEditorRef, diffModifiedRef]);

  // (Re)load whenever the modal opens on a file.
  useEffect(() => {
    // A LOCAL file has no connection, so `connId` is only required remotely —
    // requiring it always made local Edit silently never load.
    if (!isOpen || !path || (!local && !connId)) return;
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    setSaveError(null);
    setConflict(false);
    setLatest(null);
    detachDiffModels();
    setMode('edit');
    setConfirmDiscard(false);
    setContent('');
    setSavedContent('');
    backend
      .read()
      .then((res) => {
        if (cancelled) return;
        setContent(res.content);
        setSavedContent(res.content);
        setMtime(res.mtime ?? null);
        setLoading(false);
        setLoadGen((g) => g + 1);
      })
      .catch((e) => {
        if (cancelled) return;
        const token = errToken(e);
        setLoadError(
          token === 'too_large'
            ? t('fileEdit.tooLarge', 'This file is too large to edit here (2 MB limit). Download it instead.')
            : token === 'not_text'
              ? t('fileEdit.notText', 'This is not a text file.')
              : String(e?.message || e)
        );
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, local, connId, path, backend, t, detachDiffModels, setConfirmDiscard, setConflict, setContent, setLatest, setLoadError, setLoadGen, setLoading, setMode, setMtime, setSaveError, setSavedContent]);

  const doWrite = useCallback(
    async (text, expectedMtime) => {
      // A caller's validation (launch.json schema) blocks the write entirely.
      const problems = validate ? validate(text) : [];
      if (problems && problems.length) {
        setSaveError(problems.join('\n'));
        return;
      }
      setSaving(true);
      setSaveError(null);
      try {
        const res = await backend.write(text, expectedMtime);
        setContent(text);
        setSavedContent(text);
        setMtime(res?.mtime ?? null);
        setConflict(false);
        setLatest(null);
        detachDiffModels();
        setMode('edit');
        setSavedFlash(true);
        setTimeout(() => setSavedFlash(false), 1600);
        onSaved?.();
      } catch (e) {
        if (errToken(e) === 'remote_changed') {
          setConflict(true);
        } else {
          setSaveError(String(e?.message || e));
        }
      } finally {
        setSaving(false);
      }
    },
    [backend, validate, onSaved, detachDiffModels, setConflict, setContent, setLatest, setMode, setMtime, setSaveError, setSavedContent, setSavedFlash, setSaving]
  );

  // Regular save: conflict-checked against the mtime this buffer came from.
  const save = useCallback(() => {
    if (saving || loading || loadError) return;
    doWrite(contentRef.current, mtime);
  }, [doWrite, saving, loading, loadError, mtime, contentRef]);

  /** Open compare mode against the clipboard or a picked local file. */
  const startCompare = useCallback(
    async (kind) => {
      setCompareMenu(false);
      setCompareError(null);
      try {
        const source =
          kind === 'clipboard' ? await readClipboardSource() : await readLocalFileSource(name);
        setCompare(source);
        setCompareContent(source.content);
        setCompareDirty(false);
        setHunks([]);
        setMode('diff');
      } catch (e) {
        const msg = String(e?.message || e);
        // A dismissed file picker is not a failure and must not shout.
        if (msg === CANCELLED) return;
        setCompareError(
          msg === EMPTY
            ? t('fileEdit.compareEmpty', 'The clipboard has no text to compare.')
            : msg
        );
      }
    },
    [name, t, setCompare, setCompareContent, setCompareDirty, setCompareError, setCompareMenu, setHunks, setMode]
  );

  // Enter compare mode as soon as the file itself has loaded. Not before: the
  // diff needs both sides, and entering it over an empty buffer would show the
  // whole remote file as one giant "added" hunk for a moment.
  // Applied once per resolved load (see `loadGen`). Tracking the generation
  // rather than a boolean is what survives StrictMode: a cancelled first read
  // never advances the counter, so the compare is applied after the read that
  // actually landed — and "Exit compare" is not undone, because nothing changes
  // the counter again until a new file loads.
  const autoComparedGen = useRef(0);
  useEffect(() => {
    if (!isOpen || !compareWith || loadGen === 0 || loadError) return;
    if (autoComparedGen.current === loadGen) return;
    autoComparedGen.current = loadGen;
    setCompare(compareWith);
    setCompareContent(compareWith.content);
    setCompareDirty(false);
    setHunks([]);
    setMode('diff');
  }, [isOpen, compareWith, loadGen, loadError, setCompare, setCompareContent, setCompareDirty, setHunks, setMode]);

  /** Leave compare mode, keeping whatever was merged into the buffer. */
  const endCompare = useCallback(() => {
    setCompare(null);
    setCompareContent('');
    setCompareDirty(false);
    setCopied(false);
    setHunks([]);
    setCompareError(null);
    detachDiffModels();
    setMode('edit');
  }, [detachDiffModels, setCompare, setCompareContent, setCompareDirty, setCompareError, setCopied, setHunks, setMode]);

  /**
   * Pull one hunk from the left side into the buffer.
   *
   * Applied against `contentRef.current` rather than the editor model so the
   * result is identical whether it arrives from a button or a keybinding, and
   * so state stays the single source of truth. Monaco re-diffs on the value
   * change and `onDidUpdateDiff` re-derives the remaining hunks — which is why
   * nothing here tries to adjust the other hunks' line numbers by hand.
   */
  const applyHunk = useCallback(
    (change) => {
      if (!compare) return;
      setContent((prev) => applyHunkToText(prev, compareContent, change));
    },
    [compare, compareContent, setContent]
  );

  /**
   * Push one hunk the other way — from your buffer into the left-hand scratch.
   *
   * Nothing is written to the server or to disk by this: the left side is a
   * scratch copy, and the only way its result leaves the modal is the Copy
   * button. That is why the reverse direction needed Phase 2 rather than
   * arriving with the forward one — without somewhere for the result to go it
   * would have been a button that quietly did nothing useful.
   */
  const applyHunkReverseAction = useCallback(
    (change) => {
      if (!compare) return;
      setCompareContent((prev) => applyHunkReverse(prev, contentRef.current, change));
      setCompareDirty(true);
      setCopied(false);
    },
    [compare, contentRef, setCompareContent, setCompareDirty, setCopied]
  );

  const copyCompareResult = useCallback(async () => {
    try {
      await writeClipboardResult(compareContent);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch (e) {
      setCompareError(String(e?.message || e));
    }
  }, [compareContent, setCompareError, setCopied]);

  const openDiff = useCallback(async () => {
    try {
      const res = await backend.read();
      setLatest({ content: res.content, mtime: res.mtime ?? null });
      // A conflict hit while comparing: the left side becomes the server's
      // version, as it is for every other conflict. Keeping the clipboard or
      // file on the left would hide exactly the change the user must merge.
      setCompare(null);
      setCompareContent('');
      setCompareDirty(false);
      setHunks([]);
      setMode('diff');
      setConflict(false);
    } catch (e) {
      setSaveError(String(e?.message || e));
    }
  }, [backend, setCompare, setCompareContent, setCompareDirty, setConflict, setHunks, setLatest, setMode, setSaveError]);

  const requestOpenDiff = useCallback(() => {
    if (compare && compareDirty) {
      setConfirmDropLeft(true);
      return;
    }
    openDiff();
  }, [compare, compareDirty, openDiff, setConfirmDropLeft]);

  // Diff mode: save the (editable) right side, conflict-checked like every
  // other save.
  //
  // Against WHICH version: the newest server version this editor has SEEN.
  // After a conflict's "View diff" that is `latest` (the buffer was merged
  // against it) — also when a compare is started afterwards, or the save would
  // conflict a second time for nothing. Otherwise it is `mtime`, the version
  // the buffer was loaded from, exactly as for the plain Save: a compare's left
  // side (clipboard, a local file, the other pane) says nothing about the
  // server. It used to send `latest?.mtime ?? null`, and `latest` is null in a
  // plain compare: a null mtime skips the check in Rust, so saving from compare
  // overwrote a newer change on the server.
  const saveDiffResult = useCallback(() => {
    const text = diffModifiedRef.current?.getValue();
    if (text == null) return;
    setContent(text);
    const expectedMtime = latest ? latest.mtime ?? null : mtime;
    doWrite(text, expectedMtime);
  }, [doWrite, latest, mtime, diffModifiedRef, setContent]);

  // Ctrl+S always routes to the mode-appropriate save.
  saveRef.current = mode === 'diff' ? saveDiffResult : save;

  const requestClose = useCallback(() => {
    if (dirty && !loadError) {
      setConfirmDiscard(true);
      return;
    }
    detachDiffModels();
    onClose();
  }, [dirty, loadError, onClose, detachDiffModels, setConfirmDiscard]);

  // Safety net for an unmount we don't drive ourselves (the whole pane going
  // away, say). Ordering is not guaranteed here, hence detachDiffModels' guard.
  useEffect(() => () => detachDiffModels(), [detachDiffModels]);

  // Esc closes (through the dirty guard); listener only while open.
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      // The "replace the edited left side?" confirm owns Escape while it is
      // open: this listener was registered first, so without the guard it
      // would swallow the key and run the editor's close guard instead.
      if (e.key === 'Escape' && !confirmDropLeft) {
        // Monaco's find (Ctrl+F) or suggest box open: Escape is its — it closes
        // that box, and a second Escape closes the editor (owner, 06.10.2026:
        // it asked to discard the edit instead). Same rule as the shared Modal.
        if (monacoWidgetOpen(e.target)) return;
        e.stopImmediatePropagation();
        requestClose();
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [isOpen, requestClose, confirmDropLeft]);

  return {
    detachDiffModels,
    doWrite,
    save,
    startCompare,
    endCompare,
    applyHunk,
    applyHunkReverseAction,
    copyCompareResult,
    openDiff,
    requestOpenDiff,
    saveDiffResult,
    requestClose,
  };
}
